import isEqual from 'lodash/isEqual';
import moment from 'moment';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import instance from '../../../../api/instance';
import { getOrganizationExtraInfo } from '../../../../shared/db/extra-info-db';
import { getDeviceLastTrip } from '../../../../shared/db/report-db';
import { clientDb } from '../../../../shared/firebase';
import { ExtraInfoFields, iDevicePing, iFullStoreState } from '../../../../shared/interfaces';
import { localStorage } from '../../../../shared/storage';
import { iReducersState } from '../../../../stores/reducers';
import { fetchDevicesLatestPing } from '../../../../stores/reducers/devicesData/AC';
import { selectDevices } from '../../../../stores/reducers/devicesData/selector';
import { Actions as ReportActions } from "../../../../stores/reducers/report-reducers";
import { openDashboard } from '../../../../stores/reducers/dashboardInfo/AC';
import { ReactComponent as ArrowBackSvg } from '../../../../assets/svg/arrow_back.svg';
import DashboardBlock from "../../../DashboardBlock";
import Loader from '../../Billing/Other/Loader';
import FilterPrompt from '../FilterPrompt';
import DevicesTable, { IInventoryDevicesInfo } from './InventoryTable';
import styles from './Inventory.module.scss';

type FiltersType = {
    [type: string]: { [id: string]: true }
};

const Inventory = memo(() => {
    const [devicesWithInfo, setDevicesWithInfo] = useState<IInventoryDevicesInfo[]>([]);
    const filters = useSelector<iFullStoreState, FiltersType>(state => state.report.details.filters);
    const allExtraInfo = useSelector((state: iFullStoreState) => state.general.extraInfo);
    const [inventoryDeviceIds, setInventoryIds] = useState<Array<string>>([]);
    const [loading, setLoading] = useState<boolean>(true);

    const devicesDetails = useSelector(selectDevices);
    const devicesLatetsPing = useSelector((s: iReducersState) => s.devicesData.devicesLatestPing);

    const dispatch = useDispatch();

    const clearFilters = () => {
        dispatch(ReportActions.RESET_ALL_FILTERS());
        dispatch(ReportActions.RESET_DATES());
    }

    useEffect(() => {
        dispatch(fetchDevicesLatestPing());
        dispatch(openDashboard());
    }, [dispatch]);

    const fetchInventoryUnits = async (fetchParams: Record<string, string[]>) => {
        const clientId = localStorage.get('active-client');
        try {
            const params = {
                organizationKey: clientId,
                ...fetchParams
            };
            const response = await instance.post(`${process.env.REACT_APP_API_URL}/api/firestore/inventory-report`, { ...params });
            const data = response.data.data;
            setInventoryIds(Array.isArray(data) ? data : []);
        }
        catch (error) {
            console.error(error);
            setInventoryIds([]);
        }
    }

    useEffect(() => {
        const deviceIds = filters['device'] ? Object.keys(filters['device']) : [];
        const tagIds = filters['tag'] ? Object.keys(filters['tag']) : [];
        fetchInventoryUnits({ deviceIds, tagIds });
    }, [filters])

    const formatDate = (date: moment.Moment): string =>
        date ? moment(date.toDate?.() ?? date).format('MM/DD/YYYY') : '';


    const availableExtraColumns = React.useMemo(() => {
        const dynamicCols = !allExtraInfo ? [] : Object.values(allExtraInfo)
            .filter(item => !item.details.isHidden)
            .map(item => ({
                id: item.details.id,
                label: item.details.name
            }));

        const staticOptionalCols = [
            { id: 'lastTripDate', label: 'Last Trip Date' },
        ];
        const allCols = [...staticOptionalCols, ...dynamicCols];

        return allCols.sort((a, b) =>
            a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' })
        );
    }, [allExtraInfo]);

    const lastReportInfo = useMemo(() => {
        if (!Array.isArray(inventoryDeviceIds)) return {};

        return devicesLatetsPing.reduce((acc: Record<string, {
            address: string,
            street: string,
            city: string,
            state: string,
            zip: string,
            time: moment.Moment,
        }>, ping: iDevicePing, key: string) => {
            if (ping && inventoryDeviceIds.includes(key)) {
                const address = ping?.address;
                const formatted = [address?.city, address?.state, address?.street, address?.zip].filter(Boolean).join(', ');

                acc[key] = {
                    address: formatted ?? '',
                    street: address?.street ?? '',
                    city: address?.city ?? '',
                    state: address?.state ?? '',
                    zip: address?.zip ?? '',
                    time: ping?.time ?? null,
                };
            }
            return acc;
        }, {});
    }, [devicesLatetsPing, inventoryDeviceIds]);

    const devicesDetailsSize = useMemo(() => devicesDetails.size, [devicesDetails]);
    const inventoryDeviceIdsLength = useMemo(() => {
        return Array.isArray(inventoryDeviceIds) ? inventoryDeviceIds.length : 0;
    }, [inventoryDeviceIds]);

    const simplifiedDevices = useMemo(() => {
        if (!devicesDetailsSize || !inventoryDeviceIdsLength) return [];

        return inventoryDeviceIds.map(deviceId => {
            const device = devicesDetails.get(deviceId);
            if (!device) return null;

            return {
                id: device.id,
                name: device.name,
                extraInfo: device?.['extra-info'],
                photoUrl: device?.icon?.type === 2 ? device?.icon.url : '',
                externalData: device?.externalData?.location,
            };
        }).filter(Boolean);
    }, [devicesDetails, devicesDetailsSize, inventoryDeviceIds, inventoryDeviceIdsLength]);

   const getValueFromSnapshot = (val: any): string => {
        if (val === null || val === undefined) return '';

        if (typeof val === 'object') {
            if (typeof val.text === 'string') return val.text;

            if (Array.isArray(val.files)) {
                return val.files.map((f: any) => f.fileName).join(', ');
            }

            if (val.date) return val.date;
            if (val.number !== undefined) return String(val.number);
            if (val.boolean !== undefined) return val.boolean ? 'Yes' : 'No';

            return JSON.stringify(val);
        }

        return String(val);
    };

    const getInventoryDetails = useCallback(async () => {
        setLoading(true);

        if (!inventoryDeviceIdsLength) {
            setLoading(false);
            return;
        }

        try {
            const data = await getOrganizationExtraInfo();
            const keysMakeModel = [ExtraInfoFields.YEAR, ExtraInfoFields.MAKE, ExtraInfoFields.MODEL].map(key => data[key]);
            const keysType = [ExtraInfoFields.TYPE].map(key => data[key]);
            const keysAssignee = [ExtraInfoFields.ASSIGNEE].map(key => data[key]);

            const devicesWithInfoResult = await Promise.all(
                simplifiedDevices.map(async (device) => {
                    const infoSnapshot = (await clientDb().child(`/devices/device-details/${device.id}/extra-info`).once('value')).val() || {};

                    // Make (Year + Make + Model)
                    const make = keysMakeModel
                        .map(key => getValueFromSnapshot(infoSnapshot[key]))
                        .filter(Boolean)
                        .join(' ');

                    // Type
                    const type = keysType
                        .map(key => getValueFromSnapshot(infoSnapshot[key]))
                        .filter(Boolean)
                        .join(' ');


                    // Assignee
                    const assignee = keysAssignee
                        .map(key => getValueFromSnapshot(infoSnapshot[key]))
                        .find(Boolean) ?? '';

                    const lastInfo = lastReportInfo?.[device.id] ?? null;
                    const lastTrip = await getDeviceLastTrip(device.id);
                    const lastReportDate = lastInfo?.time;
                    const lastTripEndDate = lastTrip?.docs?.[0]?.data()?.endDate;

                    return {
                        id: device.id,
                        name: device.name,
                        extraInfo: device.extraInfo || null,
                        photoUrl: device.photoUrl,
                        lastPingAddress: lastInfo?.address ?? '',
                        lastPingStreet: lastInfo?.street ?? '',
                        lastPingCity: lastInfo?.city ?? '',
                        lastPingState: lastInfo?.state ?? '',
                        lastPingZip: lastInfo?.zip ?? '',
                        make,
                        type,
                        lastReportDate: formatDate(lastReportDate),
                        lastTripDate: formatDate(lastTripEndDate),
                        assignee
                    };
                })
            );

            setDevicesWithInfo(prev => {
                if (isEqual(prev, devicesWithInfoResult)) return prev;
                return devicesWithInfoResult;
            });
        } catch (error) {
            console.error("Error fetching inventory details:", error);
        } finally {
            setLoading(false);
        }
    }, [simplifiedDevices, lastReportInfo, inventoryDeviceIdsLength]);

    useEffect(() => {
        if (inventoryDeviceIdsLength) {
            getInventoryDetails();
        }
    }, [getInventoryDetails]);

    const customHeader = (
        <div className={styles.headerComponent}>
            <button className={styles.headerComponent__button} onClick={clearFilters} title="Go Back">
                <ArrowBackSvg style={{ color: '#FF7C02' }} />
            </button>
        </div>
    );

    return (
        <DashboardBlock header={customHeader}>
            {loading ? (
                <Loader />
            ) : devicesWithInfo?.length ? (
                <DevicesTable devices={devicesWithInfo} availableExtraColumns={availableExtraColumns} />
            ) : (
                <FilterPrompt />
            )}
        </DashboardBlock>
    )
})

export default Inventory;